import { from, of, concat } from 'rxjs'
import { catchError, filter, map, switchMap } from 'rxjs/operators'
import { isActionOf } from 'typesafe-actions'
import { toast } from 'react-toastify'
import {
  createRiskScoreRuleFailure,
  createRiskScoreRuleRequest,
  createRiskScoreRuleSuccess,
  getRiskScoreRulesFailure,
  getRiskScoreRulesRequest,
  getRiskScoreRulesSuccess,
  getRiskScoreRuleFailure,
  getRiskScoreRuleRequest,
  getRiskScoreRuleSuccess,
  updateRiskScoreRuleFailure,
  updateRiskScoreRuleRequest,
  updateRiskScoreRuleSuccess,
  deleteRiskScoreRuleRequest,
  deleteRiskScoreRuleSuccess,
  deleteRiskScoreRuleFailure,
} from '../actions'
import { RootEpic } from '../types'

export const createRiskScoreRuleEpic: RootEpic = (action$, state$, { createRule }) =>
  action$.pipe(
    filter(isActionOf(createRiskScoreRuleRequest)),
    switchMap((action) =>
      from(
        createRule({
          ...action.payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          if (response.success) {
            toast.success('Rule created successfully')
          }
          return createRiskScoreRuleSuccess(response)
        }),
        catchError((error) => {
          toast.error(error.message || 'Failed to create rule')
          return of(createRiskScoreRuleFailure(error.message))
        }),
      ),
    ),
  )

export const getRiskScoreRulesEpic: RootEpic = (action$, state$, { getRules }) =>
  action$.pipe(
    filter(isActionOf(getRiskScoreRulesRequest)),
    switchMap((action) =>
      from(
        getRules({
          ...action.payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => getRiskScoreRulesSuccess(response)),
        catchError((error) => {
          toast.error(error.message || 'Failed to fetch rules')
          return of(getRiskScoreRulesFailure(error.message))
        }),
      ),
    ),
  )

export const getRiskScoreRuleEpic: RootEpic = (action$, state$, { getRule }) =>
  action$.pipe(
    filter(isActionOf(getRiskScoreRuleRequest)),
    switchMap((action) =>
      from(
        getRule({
          ...action.payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => getRiskScoreRuleSuccess(response)),
        catchError((error) => {
          toast.error(error.message || 'Failed to fetch rule')
          return of(getRiskScoreRuleFailure(error.message))
        }),
      ),
    ),
  )

export const updateRiskScoreRuleEpic: RootEpic = (action$, state$, { updateRule }) =>
  action$.pipe(
    filter(isActionOf(updateRiskScoreRuleRequest)),
    switchMap((action) =>
      from(
        updateRule({
          ...action.payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => {
          if (response.success) {
            toast.success('Rule updated successfully')
            return concat(
              of(updateRiskScoreRuleSuccess(response)),
              of(getRiskScoreRulesRequest({ page: 1 })), // Refetch the list
            )
          }
          return of(updateRiskScoreRuleSuccess(response))
        }),
        switchMap((actions) => actions), // Unpack the concat observable
        catchError((error) => {
          toast.error(error.message || 'Failed to update rule')
          return of(updateRiskScoreRuleFailure(error.message))
        }),
      ),
    ),
  )

export const deleteRiskScoreRuleEpic: RootEpic = (action$, state$, { deleteRule }) =>
  action$.pipe(
    filter(isActionOf(deleteRiskScoreRuleRequest)),
    switchMap((action) =>
      from(
        deleteRule({
          ...action.payload,
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        switchMap(() => {
          toast.success('Rule deleted successfully')
          return concat(
            of(deleteRiskScoreRuleSuccess({ id: action.payload.id })),
            of(getRiskScoreRulesRequest({ page: 1 })),
          )
        }),
        catchError((error) => {
          toast.error(error.message || 'Failed to delete rule')
          return of(deleteRiskScoreRuleFailure(error.message))
        }),
      ),
    ),
  )

export default [
  createRiskScoreRuleEpic,
  getRiskScoreRulesEpic,
  getRiskScoreRuleEpic,
  updateRiskScoreRuleEpic,
  deleteRiskScoreRuleEpic,
]
